import {
  GroupedListItem,
  ListItem,
  Octopus,
} from '@maximizer/core/shared/domain';
import {
  AppointmentAttendeeGroup,
  CalendarAttendee,
} from '@maximizer/outlook/shared/domain';
import { escapeHtml } from '@maximizer/shared/util';
import { differenceInCalendarDays, subDays } from 'date-fns';

const ATTENDEE_ICONS = {
  user: 'icon-solid icon-user',
  lead: 'icon-solid icon-user-magnifying-glass',
  abEntry: 'icon-solid icon-address-book',
  company: 'icon-regular icon-building',
};

export function appointmentAttendeeIcon(
  group: AppointmentAttendeeGroup,
  isCompany = false,
): string {
  switch (group) {
    case 'lead':
      return ATTENDEE_ICONS.lead;
    case 'abentry':
      return isCompany ? ATTENDEE_ICONS.company : ATTENDEE_ICONS.abEntry;
    default:
      return ATTENDEE_ICONS.user;
  }
}

export function mapCalendarAttendeesToAppointmentAttendees(
  attendees: CalendarAttendee[],
): GroupedListItem<string>[] {
  return attendees
    .filter((attendee) => attendee.isInMaximizer && attendee.id)
    .map((attendee) => ({
      id: attendee.id ?? '',
      name: attendee.displayName || attendee.emailAddress,
      isDisabled: false,
      group: attendee.type ?? 'abentry',
      icon: appointmentAttendeeIcon(
        attendee.type === 'lead' ? 'lead' : 'abentry',
        attendee.abentryType === 'company',
      ),
    }));
}

const ATTENDEE_LIST_SPACER = '<p>&nbsp;</p>';

export function attendeesNotInMaximizer(
  attendees: CalendarAttendee[],
): CalendarAttendee[] {
  const seenEmailAddresses = new Set<string>();
  return attendees.filter((attendee) => {
    if (
      attendee.isInMaximizer ||
      seenEmailAddresses.has(attendee.emailAddress)
    ) {
      return false;
    }
    seenEmailAddresses.add(attendee.emailAddress);
    return true;
  });
}

export function prependAttendeesNotInMaximizer(
  description: string | null,
  attendees: CalendarAttendee[],
  heading: string,
): string | null {
  const emailAddresses = attendeesNotInMaximizer(attendees)
    .map((attendee) => attendee.emailAddress)
    .filter((emailAddress) => !!emailAddress);
  if (!emailAddresses.length) return description;

  const attendeeList = [
    `<p><strong>${escapeHtml(heading)}</strong></p>`,
    '<ul>',
    ...emailAddresses.map((email) => `<li>${escapeHtml(email)}</li>`),
    '</ul>',
  ].join('');

  return description
    ? `${attendeeList}${ATTENDEE_LIST_SPACER}${description}`
    : attendeeList;
}

const SENSITIVITY_API_SET = '1.14';
const MINUTES_PER_HALF_HOUR = 30;

/** The next half-hour boundary strictly after the given time, with seconds cleared. */
export function nextHalfHour(from: Date): Date {
  const next = new Date(from);
  if (next.getMinutes() < MINUTES_PER_HALF_HOUR) {
    next.setMinutes(MINUTES_PER_HALF_HOUR, 0, 0);
  } else {
    next.setMinutes(0, 0, 0);
    next.setHours(next.getHours() + 1);
  }
  return next;
}

function isLocalMidnight(date: Date): boolean {
  return (
    date.getHours() === 0 &&
    date.getMinutes() === 0 &&
    date.getSeconds() === 0 &&
    date.getMilliseconds() === 0
  );
}

// Office's item.isAllDayEvent (read: boolean, compose: IsAllDayEvent.getAsync) is
// Mailbox preview only as of 2026-09 and absent from Mailbox 1.16, so production
// clients report undefined. Once it ships in a numbered set, prefer it behind a
// typeof existence check and keep this midnight heuristic as the fallback.
export function isAllDayCalendarEvent(start: Date, end: Date): boolean {
  return (
    isLocalMidnight(start) &&
    isLocalMidnight(end) &&
    differenceInCalendarDays(end, start) >= 0
  );
}

// An all-day event ends at the following midnight, so its last day is the day before.
export function spansMultipleCalendarDays(start: Date, end: Date): boolean {
  const endsOnFollowingMidnight =
    isAllDayCalendarEvent(start, end) &&
    differenceInCalendarDays(end, start) >= 1;
  const lastDay = endsOnFollowingMidnight ? subDays(end, 1) : end;
  return differenceInCalendarDays(lastDay, start) >= 1;
}

// Sensitivity arrives with Mailbox 1.14; the add-in manifests declare 1.5.
export function supportsAppointmentSensitivity(): boolean {
  return (
    Office.context.requirements?.isSetSupported(
      'Mailbox',
      SENSITIVITY_API_SET,
    ) ?? false
  );
}

// Matches the Outlook sync, which treats both levels as a private appointment.
export function isPrivateSensitivity(
  sensitivity:
    Office.MailboxEnums.AppointmentSensitivityType | null | undefined,
): boolean {
  return (
    sensitivity === Office.MailboxEnums.AppointmentSensitivityType.Private ||
    sensitivity === Office.MailboxEnums.AppointmentSensitivityType.Confidential
  );
}

// Unknown sensitivity (host below Mailbox 1.14, or a failed read) fails closed:
// a Private meeting must never be saved as visible to others.
export function isPrivateSensitivityOrUnknown(
  sensitivity:
    Office.MailboxEnums.AppointmentSensitivityType | null | undefined,
): boolean {
  return sensitivity == null || isPrivateSensitivity(sensitivity);
}

function findLocationOptionKey(
  locationValue: string,
  locationOptions: ListItem<string>[],
): string | null {
  const normalizedValue = locationValue.toLowerCase();
  const matchedOption = locationOptions.find(
    (option) =>
      option.id === locationValue ||
      option.name.trim().toLowerCase() === normalizedValue,
  );
  return matchedOption?.id ?? null;
}

/** Accepts an option key (combobox selection) or free text (typed, or the Outlook event location). */
export function resolveAppointmentLocation(
  locationValue: Octopus.AppointmentLocationWrite | null,
  locationOptions: ListItem<string>[],
): Octopus.AppointmentLocationWrite | null {
  if (typeof locationValue === 'object') {
    return locationValue;
  }

  const trimmedValue = locationValue?.trim() ?? '';
  if (!trimmedValue) {
    return null;
  }

  return (
    findLocationOptionKey(trimmedValue, locationOptions) ?? {
      Name: trimmedValue,
      Key: { Uid: Octopus.AppointmentFreeformLocationKey },
    }
  );
}
